import React, { useEffect, useMemo, useState } from 'react';
import TableContainer from '@hud/web-components/Container/TableContainer';
import { DefaultTable, LinkCell } from '@hud/web-components/Table/Table';
import { Search } from '@hud/web-components/Search/Search';
import Pagination from '@hud/web-components/Pagination/Pagination';
import {
  createColumnHelper,
  FilterFn,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  Updater,
  useReactTable,
} from '@tanstack/react-table';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import { useGlobalQueryString } from '../../../hooks/useGlobalFilter';
import LongContentPopover from '@hud/web-components/LongContentPopover/LongContentPopover';
import Menu from '@hud/web-components/Menu/Menu';

import {
  ClearAllButton,
  FiltersIcon,
  FiltersMenuButton,
} from '../../../components/FunctionsTable/FunctionsTable.styles';
import { HeadsUpEntity, entityText, entityIcon, HeadsUpValue } from '../HeadsUpCommon';
import { formatDateTime, isDefined } from '@hud/web-components/utils';
import { CellIcon, StyledMoreItemButton, StyledMoreItemsContainer } from '../styles';
import { EntitiesList } from '../../../components/EntitiesPopover/EntitiesPopover';
import { Popover } from '@hud/web-components/Popover/Popover';
import { DEFAULT_PAGE_SIZE } from '../../../consts';
import IssuesMenuWebappContainer from '../../../components/IssuesMenuWebappContainer/IssuesMenuWebappContainer';
import { Button } from '@hud/web-components/Button/Button';
import { HudIcon } from '@hud/web-components/Icons/HudIcons';
import { useLocation, useNavigate } from 'react-router-dom';
import ServicesFilter from '../../../components/ServicesFilter/ServicesFilter';
import { useUserPreferences } from '../../../hooks/useUserPreferences';
import { stringifyTimeFrame } from '../../../utils';
import { PublicIssue as Issue } from '@hud/portal-dtos/issues-v2.dtos';
import { Entity, isEndpointEntity } from '@hud/portal-dtos/issues.dtos';
import { MultiSelectFilter } from '@hud/web-components/Filters/Filters';
import { useDebouncedValue } from '@mantine/hooks';
import { ItemCounter } from '@hud/web-components/ItemCounter/ItemCounter';

type HeadsUpTitleProps = {
  issue: Issue;
  globalUrlParams?: string;
};

export function HeadsUpTitle({ issue, globalUrlParams }: HeadsUpTitleProps) {
  return (
    <LongContentPopover>
      <CellIcon icon="other.flame" size={'20px'} />
      <LinkCell to={`/heads-up/issues/${issue.id}?${globalUrlParams}`}>
        {issue.title}
      </LinkCell>
    </LongContentPopover>
  );
}

export interface HeadsUpSourcesProps {
  entities: Entity[];
  serviceId?: number;
  flowId?: number;
  globalUrlParams: string;
}

export function HeadsUpSources({
  entities,
  serviceId,
  flowId,
  globalUrlParams,
}: HeadsUpSourcesProps) {
  const firstItem = entities[0];
  const moreItems = entities.slice(1);
  return (
    <>
      <div style={{ paddingRight: '32px' }}>
        <HeadsUpEntity
          entity={firstItem}
          globalUrlParams={globalUrlParams}
          serviceId={serviceId}
          flowId={flowId}
        />
      </div>
      {moreItems && moreItems.length > 0 && (
        <StyledMoreItemsContainer>
          <Popover
            width="300px"
            body={
              <EntitiesList>
                {moreItems.map((entity) => (
                  <HeadsUpEntity
                    key={entity.id}
                    entity={entity}
                    serviceId={serviceId}
                    flowId={flowId}
                    globalUrlParams={globalUrlParams}
                  />
                ))}
              </EntitiesList>
            }
          >
            <StyledMoreItemButton>+{moreItems.length}</StyledMoreItemButton>
          </Popover>
        </StyledMoreItemsContainer>
      )}
    </>
  );
}

export type IssuesTableProps = {
  issues: Issue[];
  rulesNames: { id: string; name: string }[];
  loading: boolean;

  currentPage: number;
  onPageChange: (page: number) => void;

  searchValue: string;
  onSearchChange: (searchValue: string) => void;

  sortingState: SortingState;
  onSortingChange: (updater: Updater<SortingState>) => void;

  filteredServices: number[];
  onFilteredServicesChange: (value: number[]) => void;
  filteredRules: string[];
  onFilteredRulesChange: (value: string[]) => void;

  filterType?: 'endpoint';
};

type FilterType = 'service' | 'rule';

interface Filter {
  type: FilterType;
  // Additional properties that each filter might need for its specific configuration
  props?: unknown; // Use more specific types based on actual requirements
}

const columnHelper = createColumnHelper<Issue>();

export default function IssuesTable({
  issues,
  rulesNames,
  loading,

  currentPage,
  onPageChange,

  searchValue,
  onSearchChange,

  sortingState,
  onSortingChange,

  filteredServices,
  onFilteredServicesChange,
  filteredRules,
  onFilteredRulesChange,

  filterType,
}: IssuesTableProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const globalUrlParams = useGlobalQueryString();
  const { userPreferredServices } = useUserPreferences();
  const [activeFilters, setActiveFilters] = useState<Filter[]>([]);
  const [ruleSearchValue, setRuleSearchValue] = useState('');
  const [debouncedRuleSearchValue] = useDebouncedValue(ruleSearchValue, 200);
  const filteredRuleOptions = useMemo(() => {
    if (!debouncedRuleSearchValue) return rulesNames;
    const search = debouncedRuleSearchValue.toLowerCase();
    return rulesNames.filter((rule) => rule.name.toLowerCase().includes(search));
  }, [rulesNames, debouncedRuleSearchValue]);

  useEffect(() => {
    if (filteredServices && filteredServices.length > 0) {
      toggleServiceFilter(true);
    }
    if (filteredRules && filteredRules.length > 0) {
      toggleRuleFilter(true);
    }
  }, [filteredServices, filteredRules]);

  const hasFilters = activeFilters.length > 0;

  const filteredIssues = useMemo(() => {
    let filtered = issues;
    if (filteredServices && filteredServices.length > 0) {
      filtered = filtered.filter((issue) => filteredServices.includes(issue.serviceId));
    }
    if (filteredRules && filteredRules.length > 0) {
      filtered = filtered.filter((issue) => filteredRules.includes(issue.ruleId));
    }
    return filtered;
  }, [issues, filteredServices, filteredRules]);

  const globalSearchFn: FilterFn<Issue> = (
    row: Row<Issue>,
    _columnId: string,
    searchTerms: string,
  ) => {
    // split the search terms by space and check if all of them are included in the row title, main entity or sources
    const issue = row.original;
    return searchTerms
      .toLowerCase()
      .split(' ')
      .every(
        (term) =>
          issue.title.toLowerCase().includes(term) ||
          entityText(issue.mainEntity).toLowerCase().includes(term) ||
          issue.serviceName.toLowerCase().includes(term) ||
          issue.sources.some((source) => entityText(source).toLowerCase().includes(term)),
      );
  };

  const handleFilterChange = (
    filterType: FilterType,
    isActive: boolean,
    filterProps = {},
  ) => {
    if (isActive) {
      if (activeFilters.some((f) => f.type === filterType)) return;
      setActiveFilters((prev) => [
        ...prev.filter((f) => f.type !== filterType),
        { type: filterType, ...filterProps },
      ]);
    } else {
      setActiveFilters((prev) => prev.filter((filter) => filter.type !== filterType));
    }
  };

  const toggleServiceFilter = (isActive: boolean) => {
    handleFilterChange('service', isActive, { ids: filteredServices });
  };

  const toggleRuleFilter = (isActive: boolean) => {
    handleFilterChange('rule', isActive, { rules: filteredRules });
  };

  const columns = useMemo(() => {
    return [
      filterType
        ? null
        : columnHelper.accessor('serviceName', {
            header: 'Service',
            size: 80,
            minSize: 80,
            maxSize: 250,
            cell: (cell) => (
              <LongContentPopover>
                <CellIcon icon="entities.servicesBlank" size={'20px'} />
                {cell.getValue()}
              </LongContentPopover>
            ),
            meta: {
              disableAutoFit: true,
            },
          }),
      filterType
        ? null
        : columnHelper.accessor('mainEntity', {
            header: 'Main entity',
            size: 140,
            minSize: 120,
            maxSize: 200,
            cell: (cell) => {
              const entity = cell.getValue() as Entity;
              return (
                <LongContentPopover>
                  <CellIcon icon={entityIcon(entity)} size={'20px'} />
                  {entityText(entity)}
                </LongContentPopover>
              );
            },
            meta: {
              disableAutoFit: true,
            },
          }),
      columnHelper.accessor('title', {
        header: 'Heads up',
        size: 180,
        minSize: 100,
        maxSize: 300,
        cell: (cell) => (
          <HeadsUpTitle
            issue={cell.row.original as Issue}
            globalUrlParams={globalUrlParams}
          />
        ),
        meta: {
          disableAutoFit: true,
        },
      }),
      columnHelper.accessor('ruleId', {
        header: 'Rule',
        size: 180,
        minSize: 100,
        maxSize: 400,
        cell: (cell) => (
          <LongContentPopover>
            {rulesNames.find((rule) => rule.id === cell.row.original.ruleId)?.name ??
              'N/A'}
          </LongContentPopover>
        ),
        meta: {
          disableAutoFit: true,
        },
      }),
      columnHelper.accessor('values', {
        header: 'Value',
        size: 140,
        minSize: 100,
        maxSize: 180,
        cell: (cell) => {
          const issue = cell.row.original;
          if (issue.values.length === 0) {
            return <LongContentPopover>N/A</LongContentPopover>;
          }
          return (
            <>
              {issue.values.map((value, index) => (
                <HeadsUpValue key={index} value={value} />
              ))}
            </>
          );
        },
        enableSorting: false,
        meta: {
          disableAutoFit: true,
        },
      }),
      columnHelper.accessor('detectionTime', {
        header: 'Detected',
        size: 90,
        minSize: 80,
        maxSize: 120,
        cell: (cell) => {
          const cellValue = cell.getValue() as Date | undefined;
          return (
            <LongContentPopover>
              {cellValue ? formatDateTime(cellValue) : '-'}
            </LongContentPopover>
          );
        },
        meta: {
          disableAutoFit: true,
        },
      }),
      columnHelper.accessor('sources', {
        header: 'Sources',
        size: 180,
        minSize: 100,
        maxSize: 400,
        cell: (cell) => {
          const issue = cell.row.original as Issue;
          if (issue.sources.length === 0) {
            return <LongContentPopover>N/A</LongContentPopover>;
          }
          const newURLParams = new URLSearchParams(globalUrlParams);
          if (issue) {
            const timeframeUrlParam = issue
              ? stringifyTimeFrame(issue.timeFrame, { normalize: true })
              : '';
            newURLParams.set('timeFrame', timeframeUrlParam);
          }
          return (
            <HeadsUpSources
              entities={cell.getValue() as Entity[]}
              serviceId={issue.serviceId}
              flowId={
                isEndpointEntity(issue.mainEntity) ? issue.mainEntity.id : undefined
              }
              globalUrlParams={newURLParams.toString()}
            />
          );
        },
        meta: {
          disableAutoFit: true,
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: (cell) => {
          const issue = cell.row.original as Issue;
          if (!isEndpointEntity(issue.mainEntity)) return null;
          return <IssuesMenuWebappContainer issue={issue} filterType={filterType} />;
        },
        size: 40,
        minSize: 40,
        maxSize: 40,
        meta: {
          disableAutoFit: true,
          rightAlign: true,
        },
      }),
    ].filter(isDefined);
  }, [globalUrlParams, rulesNames, filterType]);

  const table = useReactTable({
    data: filteredIssues,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    onSortingChange,

    columnResizeMode: 'onChange',
    state: {
      globalFilter: searchValue,
      sorting: sortingState,
      pagination: { pageIndex: currentPage - 1, pageSize: DEFAULT_PAGE_SIZE },
    },
    globalFilterFn: globalSearchFn,
    enableSortingRemoval: false,
  });

  const showCounterInFilters = !!filterType;

  return (
    <TableContainer.Container
      title={
        loading ? (
          <Skeleton width={140} />
        ) : showCounterInFilters ? (
          'Issues'
        ) : (
          `Showing ${table.getPrePaginationRowModel().rows.length}/${issues.length}`
        )
      }
      topRightSection={
        filterType ? null : (
          <Button
            onClick={() => {
              navigate(`/settings/heads-up?${globalUrlParams}`, {
                state: { from: location },
              });
            }}
          >
            <HudIcon icon="other.settings" size="20px" opacity={0.4} /> Settings
          </Button>
        )
      }
    >
      <TableContainer.Header>
        <TableContainer.Filters>
          <Search inline value={searchValue} onSearchChange={onSearchChange} />
          {activeFilters.map((filter) => {
            switch (filter.type) {
              case 'service':
                return (
                  <ServicesFilter
                    key={filter.type}
                    userPreferredServices={userPreferredServices ?? []}
                    filteredServices={filteredServices}
                    onFilteredServicesChange={onFilteredServicesChange}
                    handleFilterChange={handleFilterChange}
                  />
                );
              case 'rule':
                return (
                  <MultiSelectFilter
                    key={filter.type}
                    label="Rules"
                    multiSelectLabel={
                      filteredRules.length > 0
                        ? `${filteredRules.length} selected`
                        : 'Select...'
                    }
                    options={
                      filteredRuleOptions.map((rule) => ({
                        value: rule.id,
                        label: rule.name,
                      })) ?? []
                    }
                    selectedOptions={filteredRules}
                    onChange={onFilteredRulesChange}
                    onClear={() => {
                      onFilteredRulesChange([]);
                      handleFilterChange('rule', false);
                    }}
                    onSearch={setRuleSearchValue}
                    searchTerm={ruleSearchValue}
                    onClose={() => setRuleSearchValue('')}
                  />
                );
              default:
                return null;
            }
          })}
          <Menu.Menu>
            <Menu.Target>
              <div>
                <FiltersMenuButton>
                  <FiltersIcon
                    icon={hasFilters ? 'menu.plus' : 'other.filters'}
                    size={hasFilters ? '14px' : '20px'}
                  />
                  {!hasFilters ? 'Filters' : ''}
                </FiltersMenuButton>
              </div>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => toggleServiceFilter(true)}>Services</Menu.Item>
              <Menu.Item onClick={() => toggleRuleFilter(true)}>Rules</Menu.Item>
            </Menu.Dropdown>
          </Menu.Menu>
          {hasFilters ? (
            <ClearAllButton
              onClick={() => {
                toggleServiceFilter(false);
                toggleRuleFilter(false);
                onFilteredServicesChange?.([]);
                onFilteredRulesChange?.([]);
              }}
            >
              Clear All
            </ClearAllButton>
          ) : null}
          {showCounterInFilters ? (
            <ItemCounter total={issues.length} filtered={filteredIssues.length} />
          ) : null}
        </TableContainer.Filters>
      </TableContainer.Header>
      <HorizontalContainer>
        <DefaultTable
          table={table}
          label="Issues Table"
          loading={loading}
          loadingRows={5}
          aria-busy={loading}
        />
      </HorizontalContainer>
      <TableContainer.Footer>
        <Pagination
          // as the "total" we still want to show the total #issues, not the #issues after filtering
          pages={loading ? 0 : table.getPageCount()}
          currentPage={currentPage}
          onPageChange={onPageChange}
          loading={loading}
        />
      </TableContainer.Footer>
    </TableContainer.Container>
  );
}
